import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useAutomationRuleEntities } from './hooks/use-automation-rule-entities';
import { useGenerateAIResponseToAutomationRuleModalFormat } from './hooks/use-generate-ai-response-to-automation-rule-modal-format';
import messages from './messages';
import { generateAutomationRule } from './services/ai-rule-creator-api-service';
import style from './style.module.less';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { AutomationRulesSteps, useTrackAutomationRulesWorkflow } from '../hooks/use-track-automation-rules-workflow';

const AIRuleCreator = ({ initialPrompt = '', onPromptUsed }) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [isFromShippy, setIsFromShippy] = useState(!!initialPrompt);
  const containerRef = useRef(null);

  const { processGeneratedRule } = useGenerateAIResponseToAutomationRuleModalFormat();

  // Get available automation rule data from custom hook
  const { tags, automationRuleData } = useAutomationRuleEntities();

  const trackStart = useTrackAutomationRulesWorkflow(AutomationRulesSteps.START);
  const trackError = useTrackAutomationRulesWorkflow(AutomationRulesSteps.ERROR);

  // Memoize trimmed prompt to avoid multiple trim() calls
  const trimmedPrompt = useMemo(() => prompt.trim(), [prompt]);

  // Auto-generate when an initial prompt is provided from Shippy
  useEffect(() => {
    if (!initialPrompt) {
      return;
    }

    // Scroll to the AI creator card so user can see it
    containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

    handleGenerate(initialPrompt);
    onPromptUsed?.();
  }, [initialPrompt]); // oxlint-disable-line react-hooks/exhaustive-deps -- handleGenerate and onPromptUsed are recreated each render

  const handleGenerate = useCallback(
    async (promptToUse = trimmedPrompt) => {
      trackStart({
        prompt: promptToUse,
        isAiGenerated: true,
        source: isFromShippy ? EventSourceStrings.SHIPPY_ASSISTANT : EventSourceStrings.AI_RULE_CREATOR,
      });
      setIsGenerating(true);
      setError(null);

      // Call the API to generate the rule, passing available tags and automation rule data
      const result = await generateAutomationRule(promptToUse, tags, automationRuleData);

      if (result.success) {
        processGeneratedRule(result.data);
      } else {
        setError(result.errorMessageDescriptor);
        trackError({
          prompt: promptToUse,
          error: result.errorMessageDescriptor.defaultMessage,
          isAiGenerated: true,
        });
      }

      setIsFromShippy(false);
      setIsGenerating(false);
    },
    [trimmedPrompt, tags, automationRuleData, processGeneratedRule, trackStart, trackError, isFromShippy],
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (trimmedPrompt && !isGenerating) {
        handleGenerate();
      }
    }
  };

  return (
    <div ref={containerRef}>
      <Card className={style.container}>
        {/* Loading overlay when generating from Shippy */}
        {isFromShippy && isGenerating ? (
          <div aria-live='polite' className={style['loading-overlay']} role='status'>
            <div className={style['loading-content']}>
              <FontAwesomeIcon className={style['loading-icon']} icon={faSparkles} />
              <Text textType={TextType.H4} value={messages.generatingFromShippy} />
              <div className={style['loading-dots']}>
                <div className={style['loading-dot']} />
                <div className={style['loading-dot']} />
                <div className={style['loading-dot']} />
              </div>
            </div>
          </div>
        ) : null}

        <div className={style['header-strip']}>
          <div className={style['title-row']}>
            <FontAwesomeIcon className={style['title-sparkle-icon']} data-testid='ai-rule-creator-icon' icon={faSparkles} />
            <Text className={style.title} textType={TextType.H4} value={messages.title} />
          </div>
          <Text className={style.description} textType={TextType.Label} value={messages.description} />
        </div>

        {/* Input section */}
        <div className={style['input-section']}>
          <FloatTextArea
            className={style['prompt-input']}
            disabled={isGenerating}
            onChange={setPrompt}
            onKeyDown={handleKeyDown}
            value={prompt}
          />
        </div>

        {/* Example text and button row */}
        <div className={style['bottom-row']}>
          <div className={style['example-section']}>
            <Text className={style['example-rule']} textType={TextType.Caption} value={messages.exampleRule} />
          </div>

          <Button
            disabled={!trimmedPrompt || isGenerating}
            isLoading={isGenerating}
            onClick={() => handleGenerate()}
            theme={THEME_CONSTANTS.tertiary}
          >
            <Text value={isGenerating ? messages.generating : messages.draftRule} />
          </Button>
        </div>

        {error ? <Text className={style.error} textType={TextType.Label} value={error} /> : null}
      </Card>
    </div>
  );
};

export default AIRuleCreator;
